"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useSelectedMenuItem = exports.usePersonalMenu = exports.getUserFromPersonalMenu = void 0;
var _auth = require("@ori/auth");
var _fetching = require("@ori/fetching");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _i18n = require("@ori/i18n");
var _zod = require("zod");
var _configurationContext = require("../contexts/configurationContext");
var _logger = require("../logger");
var _menuItemSchema = require("../schemas/menuItemSchema");
var _createSessionStorageKey = require("../utils/createSessionStorageKey");
var _findSelectedPath = require("../utils/findSelectedPath");
var _isOrisalesAvailable = require("../utils/isOrisalesAvailable");
var _mapCustomerMenuToInternalMenuItems = require("../utils/mapCustomerMenuToInternalMenuItems");
var _mapOrisalesToInternalMenuItems = require("../utils/mapOrisalesToInternalMenuItems");
var _mergeOrisalesWithPersonalMenuItems = require("../utils/mergeOrisalesWithPersonalMenuItems");
var _withSessionStorageCache = require("../utils/withSessionStorageCache");
/* eslint-disable complexity -- legacy code */

const logger = _logger.mainLogger.createChild('usePersonalMenu');
const getUserFromPersonalMenu = ({
  userHash,
  customerLastName,
  customerFirstName,
  userType
}) => userHash ? {
  firstName: customerFirstName ?? null,
  lastName: customerLastName ?? null,
  userHash,
  userType
} : null;
exports.getUserFromPersonalMenu = getUserFromPersonalMenu;
const getUser = async ({
  customerId,
  globalApiClient,
  orderType
}) => {
  const {
    data
  } = await (0, _globalApiClientReact.getCustomerMenu)(globalApiClient, String(customerId), {
    orderType,
    rootLevelOnly: true
  });
  const userFromMenu = getUserFromPersonalMenu(data);
  if (!userFromMenu) {
    throw new Error('Could not get user from personal menu due to missing userHash');
  }
  return userFromMenu;
};
const getCustomerMenuItemsCached = (0, _withSessionStorageCache.withSessionStorageCache)({
  key: ({
    orderType,
    userHash
  }) => (0, _createSessionStorageKey.createSessionStorageKey)(['menu-items', orderType, userHash]),
  schema: _zod.z.array(_menuItemSchema.menuItemSchema)
}, async options => {
  const {
    customerId,
    globalApiClient,
    orderType,
    userHash
  } = options;
  const {
    data: customerMenu
  } = await (0, _globalApiClientReact.getCustomerMenu)(globalApiClient, String(customerId), {
    rootLevelOnly: false,
    orderType,
    userHash
  });
  return (0, _mapCustomerMenuToInternalMenuItems.mapCustomerMenuToInternalMenuItems)('', customerMenu.items);
});
const getCustomerOrisalesMenuCached = (0, _withSessionStorageCache.withSessionStorageCache)({
  key: ({
    consultantNumber,
    userType,
    language
  }) => (0, _createSessionStorageKey.createSessionStorageKey)(['orisales-menu-items', consultantNumber, userType, language]),
  schema: _zod.z.array(_menuItemSchema.menuItemSchema).nullable()
}, async options => {
  const {
    consultantNumber,
    globalApiClient,
    userType,
    language
  } = options;
  const canFetchOrisalesMenu = consultantNumber !== undefined && (0, _isOrisalesAvailable.isOrisalesAvailable)(userType);
  if (!canFetchOrisalesMenu) {
    return null;
  }
  const {
    data
  } = await (0, _globalApiClientReact.getApplicationsPersonalMenuOrisalesMenu)(globalApiClient, {}, {
    params: {
      distNum: consultantNumber,
      // FIXME: Why is orisales route not taking the accept language header?
      locale: language
    }
  });
  return (0, _mapOrisalesToInternalMenuItems.mapOrisalesToInternalMenuItems)('', data.items);
});
const usePersonalMenu = () => {
  const {
    customerId,
    formattedToken
  } = (0, _auth.useToken)();
  const {
    language
  } = (0, _i18n.useLanguage)();
  const configuration = (0, _configurationContext.useConfiguration)();
  const globalApiClient = (0, _globalApiClientReact.useApiClient)();
  return (0, _fetching.useSWR)(
  // Leave in formattedToken as part of the key so the globalApiClient is passed to fetcher correctly
  [`menu-items`, language, customerId, configuration, formattedToken], async ([, currentLanguage, currentCustomerId, currentConfiguration]) => {
    const {
      personalMenuInitialData: initialData,
      consultantNumber,
      consultantType,
      reportsItemName
    } = currentConfiguration;
    const {
      orderType,
      useCaching = true
    } = initialData ?? {};
    const userFromInitialData = initialData ? getUserFromPersonalMenu(initialData) : null;
    const user = userFromInitialData ?? (
    // the user must be always fetched without cache as the userHash might change when user is logged in
    await getUser({
      globalApiClient,
      customerId: currentCustomerId,
      orderType
    }));
    const customerMenuItems = await getCustomerMenuItemsCached({
      globalApiClient,
      customerId,
      orderType,
      userHash: user.userHash
    }, {
      pauseCaching: !useCaching
    });
    let orisalesMenuItems = [];

    // Orisales is being deprecated and some environments and some markets does not have enough data and API call are failing
    // Adding try/catch here prevents whole presonal menu from failing. The data are not required and the download will be tried on every request
    try {
      orisalesMenuItems = (await getCustomerOrisalesMenuCached({
        globalApiClient,
        consultantNumber,
        userType: user.userType,
        language: currentLanguage
      }, {
        pauseCaching: !useCaching
      })) ?? [];
    } catch (error) {
      logger.error(new Error('Failed to fetch the orisales menu items', {
        cause: error
      }));
    }

    // Aka personal menu
    const menuItemsWithOrisales = (0, _mergeOrisalesWithPersonalMenuItems.mergeOrisalesWithPersonalMenuItems)(customerMenuItems, orisalesMenuItems, consultantType, reportsItemName);
    return {
      user,
      customerMenuItems,
      orisalesMenuItems,
      items: menuItemsWithOrisales,
      hasSubitems: !!menuItemsWithOrisales.length || !!customerMenuItems.length || !!orisalesMenuItems?.length
    };
  }, {
    refreshInterval: 0,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false
  });
};
exports.usePersonalMenu = usePersonalMenu;
const useSelectedMenuItem = () => {
  const {
    data
  } = usePersonalMenu();
  return data?.items ? (0, _findSelectedPath.findSelectedPath)(data.items) : undefined;
};
exports.useSelectedMenuItem = useSelectedMenuItem;